<template>
  <!-- ══════════════ Template Editor Modal (3 colonnes) ══════════════ -->
  <div v-if="visible" class="redacteur-ui agent-root">
    <div class="modal-overlay tpl-editor-overlay">
      <div class="modal-panel tpl-editor-panel">
        <div class="modal-header">
          <div>
            <h3>Éditeur de template</h3>
            <span class="structure-hint">{{ headerHint }}</span>
          </div>
          <button class="icon-btn" @click="requestClose" title="Fermer l'éditeur">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </div>

        <!-- Import du .docx en cours (avant la phase de cadrage) -->
        <div v-if="isDetecting" class="tpl-uploading">
          <span class="tpl-analyzing-label">Import du modèle en cours…</span>
        </div>

        <div v-else :class="['tpl-editor-grid', { scoping: scopePhase }]">
          <!-- ── Colonne gauche : chat d'édition du template ── -->
          <section class="tpl-col tpl-col-chat">
            <div class="tpl-col-title">Modifier avec l'IA</div>
            <div class="tpl-chat-thread" ref="tplChatThread">
              <div v-if="!tplChatMessages.length && !scopePhase" class="empty-hint-sm">
                Décrivez une modification du template — ex : « fusionne les articles en un bloc répétable ».
              </div>
              <div v-for="(m, mi) in tplChatMessages" :key="mi"
                   :class="['tpl-chat-msg', m.role]">{{ m.content }}</div>
              <div v-if="scopePhase && scopeQuestions.length" class="scope-form">
                <div class="scope-form-title">Quelques questions pour cadrer le modèle</div>
                <div class="scope-form-hint">Toutes les réponses sont facultatives : répondez à ce qui compte pour vous.</div>
                <div v-for="(q, qi) in scopeQuestions" :key="'sq-' + qi" class="scope-q">
                  <div class="scope-q-text">{{ q.text }}</div>
                  <div v-if="q.kind === 'oui_non'" class="scope-q-choices">
                    <button :class="['scope-choice', { active: scopeAnswers[qi].choice === 'oui' }]"
                            :disabled="isDetectingStructure"
                            @click="toggleScopeChoice(qi, 'oui')">Oui</button>
                    <button :class="['scope-choice', { active: scopeAnswers[qi].choice === 'non' }]"
                            :disabled="isDetectingStructure"
                            @click="toggleScopeChoice(qi, 'non')">Non</button>
                    <button class="scope-choice scope-choice-more"
                            :disabled="isDetectingStructure"
                            @click="scopeAnswers[qi].showText = !scopeAnswers[qi].showText">
                      Préciser…
                    </button>
                  </div>
                  <input v-if="q.kind !== 'oui_non' || scopeAnswers[qi].showText"
                         :value="scopeAnswers[qi].text" class="modal-input scope-q-input"
                         :disabled="isDetectingStructure"
                         :placeholder="q.kind === 'oui_non' ? 'Précision (facultatif)…' : 'Votre réponse (facultatif)…'"
                         @input="scopeAnswers[qi].text = $event.target.value" />
                </div>
                <div class="scope-form-actions">
                  <button class="scope-skip-link" @click="skipScope" :disabled="isDetectingStructure"
                          title="Lancer l'analyse sans précision">Passer</button>
                  <button class="btn btn-primary" @click="submitScopeForm" :disabled="isDetectingStructure">
                    {{ isDetectingStructure ? 'Analyse en cours…' : 'Créer le template' }}
                  </button>
                </div>
              </div>
            </div>
            <div v-if="!scopePhase" class="tpl-chat-bar">
              <input v-model="tplChatInput" class="modal-input"
                     :placeholder="tplChatPlaceholder"
                     :disabled="isTplChatSending || isDetectingStructure"
                     @keydown.enter="sendTplChat" />
              <button class="btn btn-primary" @click="sendTplChat"
                      :disabled="!tplChatInput.trim() || isTplChatSending || isDetectingStructure">
                {{ (isTplChatSending || isDetectingStructure) ? '…' : 'Envoyer' }}
              </button>
            </div>
          </section>

          <!-- ── Colonne centrale : preview interactive du document ── -->
          <section class="tpl-col tpl-col-preview" ref="docPane">
            <template v-if="detectedElements">
              <div v-if="!scopePhase" class="zone-legend">
                <span class="legend-item"><span class="legend-swatch swatch-variable"></span>Variable (rempli par l'agent)</span>
                <span class="legend-item"><span class="legend-swatch swatch-fixed"></span>Fixe</span>
                <span class="legend-item"><span class="legend-swatch swatch-optional"></span>Optionnel</span>
                <label class="show-example-toggle">
                  <input type="checkbox" v-model="showExampleContent" />
                  afficher le contenu de l'exemple
                </label>
              </div>
              <div class="tpl-preview-wrap">
                <div class="doc-page tpl-doc-page">
                  <div v-if="chromeHtml(detectedChrome, 'header')" class="doc-chrome doc-chrome-header"
                       v-html="chromeHtml(detectedChrome, 'header')"></div>
                  <div @click="onDocClick" v-html="docHtml"></div>
                  <div v-if="chromeHtml(detectedChrome, 'footer')" class="doc-chrome doc-chrome-footer"
                       v-html="chromeHtml(detectedChrome, 'footer')"></div>
                </div>
                <div v-if="isDetectingStructure" class="tpl-analyzing-overlay">
                  <span class="tpl-analyzing-label">Analyse du modèle en cours…</span>
                </div>
              </div>
            </template>
            <div v-else class="tpl-no-preview">
              <template v-if="isDetectingStructure">Analyse du modèle en cours…</template>
              <template v-else-if="scopePhase">Aperçu indisponible pour ce format.</template>
              <template v-else>Aperçu indisponible pour ce format : sélectionnez un bloc via le panneau de droite.</template>
            </div>
          </section>

          <!-- ── Colonne droite : nom, sauvegarde, bloc sélectionné ── -->
          <section v-if="!scopePhase" class="tpl-col tpl-col-right">
            <div class="tpl-center-sticky">
              <input v-model="newTemplateName" placeholder="Nom du template" class="modal-input" />
              <button class="btn btn-primary tpl-save-btn" @click="saveTemplate"
                      :disabled="!newTemplateName.trim() || isSavingTemplate">
                {{ isSavingTemplate ? 'Enregistrement…' : 'Enregistrer le template' }}
              </button>
              <div class="tpl-structure-summary">{{ structureSummary }}</div>
            </div>
            <div v-if="selectedBlock" class="tpl-block-edit tpl-selected-card" :key="selectedBlockIdx">
              <div class="zone-controls-row">
                <button :class="['block-badge', selectedBlock.type]"
                        @click="toggleBlockType(selectedBlockIdx)"
                        :title="selectedBlock.type === 'variable' ? 'Variable (cliquer pour fixer)' : 'Fixe (cliquer pour rendre variable)'">
                  {{ selectedBlock.type === 'variable' ? 'V' : 'F' }}
                </button>
                <input class="block-label-input"
                       :value="selectedBlock.label"
                       @input="updateBlockLabel(selectedBlockIdx, $event.target.value)" />
                <button class="zone-delete-btn"
                        @click="deleteBlock(selectedBlockIdx)"
                        title="Supprimer ce bloc du template">
                  ✕
                </button>
              </div>
              <div class="zone-controls-row">
                <template v-if="isBlockMovable(selectedBlock)">
                  <button class="block-optional-toggle" @click="moveBlock(selectedBlockIdx, -1)"
                          title="Déplacer ce bloc vers le haut du document">↑ monter</button>
                  <button class="block-optional-toggle" @click="moveBlock(selectedBlockIdx, 1)"
                          title="Déplacer ce bloc vers le bas du document">↓ descendre</button>
                </template>
                <span v-else class="tpl-lock-note"
                      :title="selectedBlock.parent_label
                        ? 'Bloc imbriqué dans « ' + selectedBlock.parent_label + ' » : il se déplace avec son parent'
                        : 'Bloc dans un tableau : non déplaçable'">
                  🔒 {{ selectedBlock.parent_label ? 'dans « ' + selectedBlock.parent_label + ' »' : 'non déplaçable' }}
                </span>
              </div>
              <div class="zone-controls-row">
                <button :class="['block-optional-toggle', { active: selectedBlock.optional }]"
                        @click="toggleBlockOptional(selectedBlockIdx)">
                  optionnel
                </button>
                <button :class="['block-optional-toggle', { active: selectedBlock.repeatable }]"
                        @click="toggleBlockRepeatable(selectedBlockIdx)"
                        title="Bloc instanciable plusieurs fois (visa, considérant, article…)">
                  répétable
                </button>
              </div>
              <textarea class="block-instructions-input" rows="3"
                        :value="selectedBlock.instructions"
                        @input="updateBlockInstructions(selectedBlockIdx, $event.target.value)"
                        placeholder="Description du bloc : ce qu'il est, quand l'inclure, quoi y écrire…"></textarea>
              <textarea v-if="!detectedElements" class="block-textarea" rows="4"
                        :value="selectedBlock.content"
                        @input="updateBlockContent(selectedBlockIdx, $event.target.value)"></textarea>
            </div>
            <div v-else class="empty-hint-sm tpl-no-selection">
              Cliquez sur une zone du document pour afficher et modifier le bloc correspondant.
            </div>
            <div v-if="deletedBlocks.length" class="tpl-deleted-panel">
              <div class="tpl-deleted-title">Blocs supprimés</div>
              <div v-for="(b, j) in deletedBlocks" :key="'del-' + j" class="tpl-deleted-row">
                <span class="tpl-deleted-label">{{ b.parent_label ? b.label + ' · dans « ' + b.parent_label + ' »' : b.label }}</span>
                <button class="tpl-restore-btn" @click="restoreBlock(j)">Restaurer</button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Éditeur de template plein écran, extrait de RedacteurPage.vue :
// upload du .docx → phase de cadrage (QCM Oui/Non/Préciser) → détection de
// la structure → édition des zones (preview interactive + panneau droit +
// chat IA) → enregistrement (instructions générées automatiquement au save).
import { apiFetch, chromeHtml, renderDocPlain, buildDocHtml } from './shared';

export default {
  name: 'TemplateEditorModal',
  props: {
    visible: { type: Boolean, default: false },
    // Fichier .docx à importer à l'ouverture (choisi par le parent)
    file: { default: null },
  },
  data() {
    return {
      detectedStructure: null,
      detectedOriginalFile: null,
      // Blob opaque renvoyé par /templates/detect, conservé tel quel en
      // mémoire (jamais affiché ni loggé) et renvoyé à /templates/save
      detectedReasoningBlob: null,
      detectedElements: null,
      detectedParagraphs: null,
      detectedChrome: null,
      selectedBlockIdx: null,
      deletedBlocks: [],
      newTemplateName: '',
      isDetecting: false,
      isSavingTemplate: false,
      tplChatMessages: [],
      tplChatInput: '',
      isTplChatSending: false,
      showExampleContent: true,

      // Cadrage (questions de scope avant détection de la structure)
      scopePhase: false,
      scopeQuestions: [],
      scopeAnswers: [],
      scopeNotes: null,
      isDetectingStructure: false,
    };
  },

  computed: {
    headerHint() {
      if (this.isDetecting) return 'Import du document en cours…';
      return this.scopePhase
        ? 'Répondez aux questions de cadrage dans le chat (ou passez) pour lancer l\'analyse du modèle'
        : 'Cliquez sur une zone du document pour la modifier dans le panneau de droite';
    },
    selectedBlock() {
      if (this.selectedBlockIdx === null || !this.detectedStructure) return null;
      return this.detectedStructure[this.selectedBlockIdx] || null;
    },
    structureSummary() {
      const blocks = this.detectedStructure || [];
      const variables = blocks.filter((b) => b.type === 'variable').length;
      return `${blocks.length} blocs détectés — ${variables} variables, ${blocks.length - variables} fixes`;
    },
    // Paragraph ids des blocs supprimés (envoyés au backend en deleted_pids).
    // Dédupliqués : les pids d'un enfant imbriqué sont inclus dans ceux de
    // son parent, la suppression en cascade les compterait deux fois
    deletedPids() {
      return Array.from(new Set(this.deletedBlocks.reduce(
        (acc, b) => acc.concat(b.paragraph_indices || []), [],
      )));
    },
    deletedPidSet() {
      return new Set(this.deletedPids);
    },
    // Paragraphes en cellule de tableau (ctx non null) → bloc verrouillé
    lockedPidSet() {
      const locked = new Set();
      if (this.detectedParagraphs && this.detectedParagraphs.length) {
        this.detectedParagraphs.forEach((p) => { if (p.ctx) locked.add(p.idx); });
        return locked;
      }
      // Fallback sans `paragraphs` : on déduit les cellules du rendu
      const markAll = (items, inTable) => {
        for (const el of items || []) {
          if (el.kind === 'table') {
            for (const row of el.rows || []) {
              for (const cell of row) markAll(cell.items, true);
            }
          } else if (inTable) locked.add(el.idx);
        }
      };
      if (this.detectedElements) markAll(this.detectedElements, false);
      return locked;
    },
    // Éléments top-level réordonnés selon l'ordre du tableau structure —
    // permutation par emplacements, mêmes sémantiques que le backend à la
    // sauvegarde : chaque bloc déplaçable (pris dans l'ordre du tableau)
    // prend la place du bloc déplaçable de même rang dans le document ;
    // tableaux et paragraphes hors-blocs restent strictement en place.
    orderedDetectedElements() {
      if (!this.detectedElements) return null;
      const structure = this.detectedStructure || [];
      const pidToMovable = {};
      const movableBis = [];
      structure.forEach((b, bi) => {
        if (!this.isBlockMovable(b)) return;
        movableBis.push(bi);
        (b.paragraph_indices || []).forEach((pi) => { pidToMovable[pi] = bi; });
      });
      if (!movableBis.length) return this.detectedElements;
      const segments = {};
      const slotOrder = []; // bis des blocs déplaçables dans l'ordre du doc
      const out = [];
      this.detectedElements.forEach((el) => {
        const bi = el.kind === 'table' ? undefined : pidToMovable[el.idx];
        if (bi === undefined) { out.push(el); return; }
        if (!(bi in segments)) {
          slotOrder.push(bi);
          segments[bi] = [];
          out.push({ slot: true });
        }
        segments[bi].push(el);
      });
      // Ordre cible restreint aux blocs réellement présents au top-level
      const targetOrder = movableBis.filter((bi) => bi in segments);
      if (slotOrder.every((bi, k) => bi === targetOrder[k])) {
        return this.detectedElements;
      }
      const result = [];
      let s = 0;
      out.forEach((el) => {
        if (el.slot) { result.push(...segments[targetOrder[s]]); s += 1; } else result.push(el);
      });
      return result;
    },
    tplChatPlaceholder() {
      if (this.isDetectingStructure) return 'Analyse du modèle en cours…';
      if (this.scopePhase) return 'Votre réponse (facultatif : vous pouvez passer)…';
      return this.isTplChatSending ? 'Modification en cours…' : 'Modifier le template avec l\'IA…';
    },
    docHtml() {
      const elements = this.orderedDetectedElements;
      if (!elements) return '';
      // Phase de cadrage : document rendu sans zones (structure pas encore détectée)
      if (this.scopePhase || !this.detectedStructure) {
        return renderDocPlain(elements);
      }
      return buildDocHtml(
        elements, this.detectedStructure || [],
        this.selectedBlockIdx, this.showExampleContent,
        this.deletedPidSet, true, this.isBlockMovable,
      );
    },
  },

  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.resetTemplateEditor();
        if (val && this.file) this.startUpload(this.file);
      },
    },
  },

  methods: {
    chromeHtml,

    // ── Import du .docx (ex handleTemplateUpload) ────
    async startUpload(file) {
      this.isDetecting = true;
      try {
        // JSON base64 plutôt que multipart : en prod, le WAF bloque les
        // uploads multipart de .docx vers l'API, alors que le même contenu
        // en base64 passe (transport identique à /templates/detect).
        const bytes = new Uint8Array(await file.arrayBuffer());
        let bin = '';
        const CHUNK = 0x8000;
        for (let i = 0; i < bytes.length; i += CHUNK) {
          bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
        }
        const data = await apiFetch('/redacteur/api/v1/templates', {
          method: 'POST',
          body: JSON.stringify({ file_b64: btoa(bin), filename: file.name }),
        });
        // L'éditeur a pu être fermé pendant l'upload : on ignore la réponse
        if (!this.visible) return;
        // L'upload ne renvoie plus la structure : elle arrive après la
        // phase de cadrage, via /templates/detect
        this.detectedStructure = null;
        this.detectedOriginalFile = data.original_file || null;
        this.detectedElements = data.document_elements || null;
        this.detectedParagraphs = data.paragraphs || null;
        this.detectedChrome = data.document_chrome || null;
        this.scopeQuestions = (data.scope_questions || []).map((q) =>
          (typeof q === 'string' ? { text: q, kind: 'libre' } : q));
        this.scopeAnswers = this.scopeQuestions.map(() =>
          ({ choice: null, text: '', showText: false }));
        this.scopeNotes = null;
        this.scopePhase = true;
        this.scrollTplChat();
        // Pas de question de cadrage : on lance la détection directement
        if (!this.scopeQuestions.length) this.runScopeDetect(null);
      } catch (e) {
        alert('Erreur : ' + e.message);
        this.$emit('close');
      } finally { this.isDetecting = false; }
    },

    // ── Cadrage (scope) puis détection de la structure ──
    toggleScopeChoice(qi, val) {
      const a = this.scopeAnswers[qi];
      a.choice = a.choice === val ? null : val;
    },

    submitScopeForm() {
      if (this.isDetectingStructure) return undefined;
      const pairs = this.scopeQuestions
        .map((q, i) => {
          const a = this.scopeAnswers[i] || {};
          const parts = [];
          if (a.choice === 'oui') parts.push('Oui');
          if (a.choice === 'non') parts.push('Non');
          const txt = (a.text || '').trim();
          if (txt) parts.push(txt);
          return { q: q.text, a: parts.join(', ') };
        })
        .filter((p) => p.a);
      // trace lisible dans le fil, pour la suite de la conversation
      if (pairs.length) {
        this.tplChatMessages.push({
          role: 'user',
          content: pairs.map((p) => `${p.q}\n→ ${p.a}`).join('\n\n'),
        });
        this.scrollTplChat();
      }
      const scope = pairs.length
        ? pairs.map((p) => `Q : ${p.q}\nR : ${p.a}`).join('\n')
        : null;
      return this.runScopeDetect(scope);
    },

    skipScope() {
      if (this.isDetectingStructure) return undefined;
      return this.runScopeDetect(null);
    },

    async runScopeDetect(scope) {
      this.scopeNotes = scope;
      this.isDetectingStructure = true;
      try {
        const data = await apiFetch('/redacteur/api/v1/templates/detect', {
          method: 'POST',
          body: JSON.stringify({
            original_file: this.detectedOriginalFile,
            scope,
          }),
        });
        // L'éditeur a pu être fermé pendant l'appel (~60 s) : on ignore
        // la réponse pour ne pas le rouvrir avec un état périmé
        if (!this.scopePhase) return;
        this.detectedStructure = data.structure || [];
        // Blob opaque à round-tripper vers /templates/save (jamais
        // affiché ni loggé)
        this.detectedReasoningBlob = data.reasoning_blob !== undefined
          ? data.reasoning_blob : null;
        this.scopePhase = false;
        this.tplChatMessages.push({
          role: 'assistant',
          content: 'Le modèle est prêt : ajustez les zones dans le document, ou décrivez-moi une modification.',
        });
        this.scrollTplChat();
      } catch (e) {
        // Retour en phase de cadrage : l'utilisateur peut réessayer ou passer
        alert('Erreur : ' + e.message);
      } finally {
        this.isDetectingStructure = false;
      }
    },

    resetTemplateEditor() {
      this.detectedStructure = null;
      this.detectedOriginalFile = null;
      this.detectedReasoningBlob = null;
      this.detectedElements = null;
      this.detectedParagraphs = null;
      this.detectedChrome = null;
      this.selectedBlockIdx = null;
      this.deletedBlocks = [];
      this.tplChatMessages = [];
      this.tplChatInput = '';
      this.newTemplateName = '';
      this.scopePhase = false;
      this.scopeQuestions = [];
      this.scopeAnswers = [];
      this.scopeNotes = null;
      this.isDetectingStructure = false;
    },

    requestClose() {
      if (!confirm('Fermer l\'éditeur ? La structure détectée sera perdue.')) return;
      this.$emit('close');
    },

    // ── Sélection (preview → panneau de droite) ─────
    selectBlock(i, origin) {
      this.selectedBlockIdx = i;
      if (origin === 'preview') return;
      this.$nextTick(() => {
        const pane = this.$refs.docPane;
        const zone = pane && pane.querySelector(`[data-bi="${i}"]`);
        if (zone) zone.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    },

    // ── Document view (zones) ───────────────────────
    onDocClick(e) {
      // Phase de cadrage : pas de zones, pas d'édition
      if (this.scopePhase || !this.detectedStructure) return;
      const actionBtn = e.target.closest ? e.target.closest('[data-action]') : null;
      if (actionBtn) {
        const azone = actionBtn.closest('[data-bi]');
        if (!azone) return;
        const bi = Number(azone.getAttribute('data-bi'));
        const action = actionBtn.getAttribute('data-action');
        if (action === 'delete') this.deleteBlock(bi);
        else if (action === 'toggle-type') this.toggleBlockType(bi);
        else if (action === 'toggle-optional') this.toggleBlockOptional(bi);
        else if (action === 'toggle-repeatable') this.toggleBlockRepeatable(bi);
        else if (action === 'move-up') this.moveBlock(bi, -1);
        else if (action === 'move-down') this.moveBlock(bi, 1);
        return;
      }
      const zone = e.target.closest ? e.target.closest('[data-bi]') : null;
      if (zone) {
        this.selectBlock(Number(zone.getAttribute('data-bi')), 'preview');
        return;
      }
      const free = e.target.closest ? e.target.closest('[data-pidx]') : null;
      if (free) {
        const idx = Number(free.getAttribute('data-pidx'));
        const tmp = document.createElement('div');
        tmp.innerHTML = free.innerHTML;
        const text = (tmp.textContent || '').trim();
        const at = this.insertBlockAtDocRank({
          label: text.slice(0, 30) || 'Nouveau bloc',
          type: 'fixed',
          role: 'autre',
          content: text,
          instructions: '',
          paragraph_indices: [idx],
          optional: true,
          repeatable: false,
          unit_size: 1,
        });
        this.selectBlock(at, 'preview');
        return;
      }
      this.selectedBlockIdx = null;
    },

    // Insère un bloc à son rang document (min des paragraph_indices) dans
    // la structure — un push en fin déclencherait une permutation backend
    // involontaire à la sauvegarde (l'ordre du tableau = l'ordre du doc)
    insertBlockAtDocRank(block) {
      const minPid = (b) => Math.min(...(b.paragraph_indices || []));
      const pid = minPid(block);
      let at = this.detectedStructure.findIndex((b) => minPid(b) > pid);
      if (at === -1) at = this.detectedStructure.length;
      this.detectedStructure.splice(at, 0, block);
      if (this.selectedBlockIdx !== null && at <= this.selectedBlockIdx) {
        this.selectedBlockIdx += 1;
      }
      return at;
    },

    // ── Réordonnancement des blocs ──────────────────
    isBlockMovable(block) {
      // Bloc enfant imbriqué (parent_label) : il se déplace avec son
      // parent, jamais seul (le backend le marque aussi movable=false)
      if (block.movable === false || block.parent_label) return false;
      const locked = this.lockedPidSet;
      return (block.paragraph_indices || []).every((pi) => !locked.has(pi));
    },

    // Échange le bloc avec le bloc déplaçable adjacent (dans l'ordre du
    // tableau structure = ordre du document à la sauvegarde)
    moveBlock(i, dir) {
      const arr = this.detectedStructure;
      if (!arr || !this.isBlockMovable(arr[i])) return;
      let j = i + dir;
      while (j >= 0 && j < arr.length && !this.isBlockMovable(arr[j])) j += dir;
      if (j < 0 || j >= arr.length) return;
      const [moved] = arr.splice(i, 1);
      arr.splice(j, 0, moved);
      this.selectedBlockIdx = j;
    },

    // ── Suppression / restauration de blocs ─────────
    deleteBlock(i) {
      const arr = this.detectedStructure;
      const block = arr[i];
      if (!block) return;
      // Bloc parent : ses enfants imbriqués (parent_label) partent avec
      // lui, leurs pids rejoignent aussi deletedPids
      const indices = [i];
      if (block.label && !block.parent_label) {
        arr.forEach((b, bi) => {
          if (bi !== i && b.parent_label && b.parent_label === block.label) indices.push(bi);
        });
      }
      indices.sort((a, b) => a - b);
      const removed = indices.map((bi) => arr[bi]);
      for (let k = indices.length - 1; k >= 0; k -= 1) arr.splice(indices[k], 1);
      this.deletedBlocks.push(...removed);
      if (this.selectedBlockIdx !== null) {
        if (indices.indexOf(this.selectedBlockIdx) !== -1) this.selectedBlockIdx = null;
        else {
          this.selectedBlockIdx -= indices
            .filter((bi) => bi < this.selectedBlockIdx).length;
        }
      }
    },

    restoreBlock(j) {
      const block = this.deletedBlocks[j];
      if (!block) return;
      // Restauration groupée pour les blocs imbriqués : les pids d'un
      // enfant sont inclus dans ceux de son parent, restaurer l'un sans
      // l'autre laisserait des pids orphelins dans deletedPids. Un enfant
      // dont le parent est toujours en place se restaure seul.
      const group = [block];
      let parent = null;
      if (block.parent_label) {
        parent = this.deletedBlocks.find((b) => b.label === block.parent_label) || null;
        if (parent) group.push(parent);
      } else if ((block.children || []).length) {
        parent = block;
      }
      if (parent) {
        this.deletedBlocks.forEach((b) => {
          if (group.indexOf(b) === -1
              && b.parent_label && b.parent_label === parent.label) group.push(b);
        });
      }
      this.deletedBlocks = this.deletedBlocks.filter((b) => group.indexOf(b) === -1);
      group.forEach((b) => this.insertBlockAtDocRank(b));
    },

    // ── Chat d'édition du template ──────────────────
    async sendTplChat() {
      const text = (this.tplChatInput || '').trim();
      if (!text || this.isTplChatSending) return;
      this.tplChatMessages.push({ role: 'user', content: text });
      this.tplChatInput = '';
      this.isTplChatSending = true;
      this.scrollTplChat();
      try {
        const result = await apiFetch('/redacteur/api/v1/templates/chat', {
          method: 'POST',
          body: JSON.stringify({
            messages: this.tplChatMessages.map((m) => ({ role: m.role, content: m.content })),
            structure: this.detectedStructure,
            original_file: this.detectedOriginalFile,
          }),
        });
        // La structure peut revenir réordonnée (tool de réordonnancement) :
        // on ré-applique l'ordre reçu tel quel.
        if (result.structure) this.detectedStructure = result.structure;
        this.selectedBlockIdx = null;
        this.tplChatMessages.push({ role: 'assistant', content: result.content || 'Fait.' });
      } catch (e) {
        this.tplChatMessages.push({ role: 'assistant', content: 'Erreur : ' + e.message });
      } finally {
        this.isTplChatSending = false;
        this.scrollTplChat();
      }
    },

    scrollTplChat() {
      this.$nextTick(() => {
        const el = this.$refs.tplChatThread;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    // ── Édition des blocs (panneau droit) ───────────
    toggleBlockType(i) {
      const block = this.detectedStructure[i];
      this.$set(this.detectedStructure, i, {
        ...block,
        type: block.type === 'variable' ? 'fixed' : 'variable',
      });
    },

    toggleBlockOptional(i) {
      const block = this.detectedStructure[i];
      this.$set(this.detectedStructure, i, {
        ...block,
        optional: !block.optional,
      });
    },

    toggleBlockRepeatable(i) {
      const block = this.detectedStructure[i];
      this.$set(this.detectedStructure, i, {
        ...block,
        repeatable: !block.repeatable,
        unit_size: block.unit_size || 1,
      });
    },

    updateBlockLabel(i, value) {
      const block = this.detectedStructure[i];
      const oldLabel = block.label;
      this.$set(this.detectedStructure, i, { ...block, label: value });
      if (!oldLabel || oldLabel === value) return;
      // La hiérarchie imbriquée est liée par LABEL (parent_label sur les
      // enfants, children sur le parent) : propager le renommage, sinon la
      // cascade de suppression, la restauration groupée et l'escamotage des
      // zones enfants (childBis) perdent la liaison. On synchronise aussi
      // les blocs supprimés (un enfant supprimé seul doit pouvoir retrouver
      // son parent renommé à la restauration).
      const syncLinks = (b, setter) => {
        if (b.parent_label === oldLabel) setter({ ...b, parent_label: value });
        else if ((b.children || []).indexOf(oldLabel) !== -1) {
          setter({
            ...b,
            children: b.children.map((l) => (l === oldLabel ? value : l)),
          });
        }
      };
      this.detectedStructure.forEach((b, bi) => {
        if (bi !== i) syncLinks(b, (nb) => this.$set(this.detectedStructure, bi, nb));
      });
      this.deletedBlocks.forEach((b, bj) => {
        syncLinks(b, (nb) => this.$set(this.deletedBlocks, bj, nb));
      });
    },

    updateBlockContent(i, value) {
      const block = this.detectedStructure[i];
      this.$set(this.detectedStructure, i, { ...block, content: value });
    },

    updateBlockInstructions(i, value) {
      const block = this.detectedStructure[i];
      this.$set(this.detectedStructure, i, { ...block, instructions: value });
    },

    // ── Enregistrement ──────────────────────────────
    async saveTemplate() {
      if (!this.newTemplateName.trim() || !this.detectedStructure || this.isSavingTemplate) return;
      this.isSavingTemplate = true;
      try {
        const payload = {
          name: this.newTemplateName,
          // L'ordre du tableau structure devient l'ordre du document
          structure: this.detectedStructure,
          deleted_pids: this.deletedPids,
          scope_notes: this.scopeNotes,
        };
        if (this.detectedOriginalFile) {
          payload.original_file = this.detectedOriginalFile;
        }
        if (this.detectedReasoningBlob != null) {
          payload.reasoning_blob = this.detectedReasoningBlob;
        }
        const data = await apiFetch('/redacteur/api/v1/templates/save', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        // Le save renvoie le template créé avec ses instructions (skill)
        // auto-générées : le parent ferme l'éditeur et rafraîchit sa liste
        this.$emit('saved', { ...(data || {}), name: this.newTemplateName });
      } catch (e) { alert('Erreur : ' + e.message); }
      finally { this.isSavingTemplate = false; }
    },
  },
};
</script>

<style src="./redacteur-ui.css"></style>
